import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

import { SurveyViewFormValues, UseSurveyFormProps } from "../types";
import {
  SurveyViewDataFormat,
  SurveyViewDistributionFormat,
  SurveyViewPublicationDestination,
} from "@/redux/survey-views/types";

export const useSurveyViewForm = (): UseSurveyFormProps => {
  const { t } = useTranslation();

  const surveyViewSchema = yup.object().shape({
    id: yup.string().typeError(t("form.validation.notValid")).nullable(),
    survey: yup
      .number()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    title: yup
      .string()
      .typeError(t("form.validation.notValid"))
      .required(t("form.validation.required")),
    description: yup.string().typeError(t("form.validation.notValid")),
    config: yup.object().shape({
      dataFormat: yup
        .string()
        .typeError(t("form.validation.notValid"))
        .oneOf(
          [SurveyViewDataFormat.RAW, SurveyViewDataFormat.DESCRIPTIVE],
          t("form.validation.notValid"),
        )
        .required(t("form.validation.required")),
      distributionFormat: yup
        .string()
        .typeError(t("form.validation.notValid"))
        .oneOf(
          [
            SurveyViewDistributionFormat.DIGITAL,
            SurveyViewDistributionFormat.PRINT,
          ],
          t("form.validation.notValid"),
        )
        .required(t("form.validation.required")),
      includeFrontCover: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includeBackCover: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includeTableOfContent: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includePrmePage: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includeSdgPage: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includeOrganizationSummary: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includeContributors: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      includeDividers: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      grayScale: yup
        .boolean()
        .typeError(t("form.validation.notValid"))
        .default(false),
      coverPageBackgroundColor: yup
        .string()
        .typeError(t("form.validation.notValid")),
      publicationDestination: yup
        .string()
        .typeError(t("form.validation.notValid"))
        .default(SurveyViewPublicationDestination.EXTERNAL),
      contributors: yup
        .array()
        .of(yup.number().required().typeError(t("form.validation.notValid")))
        .default([]),
    }),
    answers: yup
      .array()
      .of(
        yup.object().shape({
          answer: yup
            .number()
            .typeError(t("form.validation.notValid"))
            .required(t("form.validation.required")),
          attachedBlocks: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.number().typeError(t("form.validation.notValid")),
                sharingRights: yup
                  .string()
                  .typeError(t("form.validation.notValid")),
              }),
            )
            .default([]),
        }),
      )
      .min(1, t("form.validation.required")),
  });

  const form = useForm<SurveyViewFormValues, null, SurveyViewFormValues>({
    // @ts-expect-error state is same as schema
    resolver: yupResolver(surveyViewSchema),
    mode: "onChange",
    defaultValues: {
      id: undefined,
      survey: undefined,
      title: "",
      description: "",
      config: {
        includeFrontCover: true,
        includeBackCover: true,
        includeTableOfContent: true,
        includePrmePage: true,
        includeSdgPage: true,
        includeOrganizationSummary: false,
        includeContributors: true,
        includeDividers: true,
        grayScale: false,
        dataFormat: SurveyViewDataFormat.RAW,
        distributionFormat: undefined,
        coverPageBackgroundColor: "#1E3250",
        publicationDestination: undefined,
        contributors: [],
      },
      answers: [],
    },
  });

  return { form };
};
